import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  Check,
  ChevronDown,
  Filter,
  Heart,
  History,
  ImagePlus,
  Search,
  ShieldCheck,
  ShoppingBag,
  SlidersHorizontal,
  Sparkles,
  Store,
  X,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import BusinessFooter from "../components/BusinessFooter";
import MarketplacePreviewCard from "../components/MarketplacePreviewCard";
import PaymentWidget from "../components/PaymentWidget";
import RefundPolicy from "../components/RefundPolicy";
import ServerCard from "../components/ServerCard";
import ServerCardStudio from "./ServerCardStudio";

const API_ORIGIN =
  typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "https://disparty.com"
    : "";

const SORT_OPTIONS = [
  { id: "popular", label: "인기순" },
  { id: "newest", label: "최신순" },
  { id: "price-low", label: "낮은 가격순" },
  { id: "price-high", label: "높은 가격순" },
];

async function apiRequest(path, options = {}) {
  const response = await fetch(`${API_ORIGIN}${path}`, {
    credentials: "include",
    ...options,
    headers: options.body instanceof FormData
      ? options.headers
      : { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || data.message || "요청을 처리하지 못했습니다.");
  }
  return data;
}

function formatPrice(value) {
  return `${Number(value || 0).toLocaleString()}원`;
}

function formatDate(value) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("ko-KR", { dateStyle: "medium" }).format(new Date(value));
}

function serverPreviewData(server) {
  if (!server) return undefined;
  const extension = server.icon?.startsWith("a_") ? "gif" : "png";
  return {
    server_name: server.server_name,
    description: server.description,
    iconUrl: server.icon
      ? `https://cdn.discordapp.com/icons/${server.server_id}/${server.icon}.${extension}`
      : "",
  };
}

function SelectMenu({ value, options, onChange, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const current = options.find((option) => option.id === value) || options[0];

  useEffect(() => {
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event) => event.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", escape);
    };
  }, []);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex h-11 min-w-[142px] items-center justify-between gap-3 rounded-lg border border-[#3a3c44] bg-[#24252a] px-4 text-sm font-semibold text-gray-100 transition-colors hover:border-[#5865F2]"
      >
        {current.label}
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            role="listbox"
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 top-full z-50 mt-2 w-48 overflow-hidden rounded-lg border border-[#3a3c44] bg-[#202126] p-1 shadow-2xl"
          >
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={option.id === value}
                onClick={() => {
                  onChange(option.id);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left text-sm transition-colors ${
                  option.id === value ? "bg-[#5865F2] text-white" : "text-gray-300 hover:bg-white/5"
                }`}
              >
                {option.label}
                {option.id === value && <Check className="h-4 w-4" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ProductTile({ product, onOpen, onFavorite, loggedIn }) {
  return (
    <article className="group relative flex min-w-0 flex-col overflow-hidden rounded-lg border border-[#373940] bg-[#24252a] transition-[border-color,transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:border-[#555966] hover:shadow-xl">
      <button type="button" onClick={onOpen} className="relative block w-full overflow-hidden bg-[#191a1e] p-4 text-left">
        <MarketplacePreviewCard product={product} compact />
      </button>
      <button
        type="button"
        aria-label={product.isFavorite ? "찜 해제" : "찜하기"}
        title={loggedIn ? (product.isFavorite ? "찜 해제" : "찜하기") : "로그인 후 찜할 수 있어요"}
        onClick={onFavorite}
        className={`absolute right-5 top-5 z-40 flex h-10 w-10 items-center justify-center rounded-lg border shadow-lg transition-colors ${
          product.isFavorite
            ? "border-rose-400/50 bg-rose-500 text-white"
            : "border-white/10 bg-[#15161a]/90 text-gray-300 hover:text-white"
        }`}
      >
        <Heart className={`h-5 w-5 ${product.isFavorite ? "fill-current" : ""}`} />
      </button>
      <button type="button" onClick={onOpen} className="flex flex-1 flex-col px-4 pb-4 pt-1 text-left">
        <h3 className="line-clamp-1 text-base font-bold text-white">{product.name}</h3>
        <p className="mt-1 truncate text-xs text-gray-500">{product.seller?.displayName || "Disparty"}</p>
        <div className="mt-3 flex items-end justify-between gap-2">
          <strong className="text-lg text-white">{formatPrice(product.price)}</strong>
          <div className="flex items-center gap-2">
            {product.isNew && (
              <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-black text-[#202126]">NEW</span>
            )}
            {product.isOwned && (
              <span className="rounded-md bg-emerald-500/15 px-2 py-1 text-[11px] font-bold text-emerald-300">보유 중</span>
            )}
          </div>
        </div>
      </button>
    </article>
  );
}

function ServerSelector({ servers, value, onChange }) {
  const options = servers.map((server) => ({ id: server.server_id, label: server.server_name }));
  if (!options.length) {
    return <p className="rounded-lg border border-amber-400/20 bg-amber-400/10 p-3 text-sm text-amber-200">먼저 내 서버 관리에서 서버를 등록해주세요.</p>;
  }
  return <SelectMenu value={value} options={options} onChange={onChange} ariaLabel="적용할 서버 선택" />;
}

function ProductDetail({
  product,
  user,
  servers,
  selectedServerId,
  onServerChange,
  onClose,
  onFavorite,
  onBuy,
  onApply,
  paymentOrder,
  working,
  message,
}) {
  const [purchaseConsents, setPurchaseConsents] = useState({
    purchaseTermsAccepted: false,
    digitalSupplyConsent: false,
    intermediaryNoticeAccepted: false,
  });
  const selectedServer = servers.find((server) => server.server_id === selectedServerId);
  const previewServer = selectedServer
    ? { ...selectedServer, shopProduct: product }
    : null;

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    setPurchaseConsents({
      purchaseTermsAccepted: false,
      digitalSupplyConsent: false,
      intermediaryNoticeAccepted: false,
    });
  }, [product.id]);

  const setConsent = (field, checked) => {
    setPurchaseConsents((current) => ({ ...current, [field]: checked }));
  };
  const allPurchaseConsentsAccepted = Object.values(purchaseConsents).every(Boolean);
  const seller = product.seller || {};
  const isPlatformProduct = !product.sellerId || seller.sellerType === "PLATFORM";
  const isBusinessSeller = seller.sellerType === "BUSINESS";

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[1200] flex bg-black/80 p-0 backdrop-blur-sm md:p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <motion.section
        role="dialog"
        aria-modal="true"
        aria-label={`${product.name} 상품 상세`}
        initial={{ opacity: 0, y: 18, scale: 0.99 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12 }}
        transition={{ duration: 0.16 }}
        className="relative m-auto grid h-full w-full max-w-6xl overflow-hidden bg-[#17181c] shadow-2xl md:h-[min(820px,calc(100vh-48px))] md:grid-cols-[minmax(320px,0.9fr)_minmax(420px,1.45fr)] md:rounded-lg md:border md:border-[#373940]"
      >
        <aside className="order-2 overflow-y-auto border-t border-[#35363c] bg-[#303137] p-5 md:order-1 md:border-r md:border-t-0 md:p-7">
          <div className="mx-auto max-w-sm md:hidden">
            <MarketplacePreviewCard product={product} server={serverPreviewData(selectedServer)} compact />
          </div>
          <div className="mt-6 md:mt-0">
            <div className="flex items-center gap-2 text-xs font-bold uppercase text-[#9ba3ff]">
              <Sparkles className="h-4 w-4" /> 서버 카드
            </div>
            <h2 className="mt-3 text-2xl font-black text-white">{product.name}</h2>
            <button type="button" className="mt-2 flex items-center gap-2 text-sm text-gray-400">
              {product.seller?.avatarUrl ? (
                <img src={product.seller.avatarUrl} alt="" className="h-6 w-6 rounded-full" />
              ) : (
                <Store className="h-5 w-5" />
              )}
              {product.seller?.displayName || "Disparty"}
            </button>
            <p className="mt-5 whitespace-pre-line text-sm leading-6 text-gray-200">{product.description}</p>
            {product.tags?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {product.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-black/20 px-3 py-1 text-xs text-gray-300">#{tag}</span>
                ))}
              </div>
            )}
            <div className="mt-7 flex items-center justify-between border-t border-white/10 pt-5">
              <strong className="text-2xl text-white">{formatPrice(product.price)}</strong>
              <button
                type="button"
                onClick={onFavorite}
                className={`flex h-11 w-11 items-center justify-center rounded-lg border ${product.isFavorite ? "border-rose-400 bg-rose-500 text-white" : "border-white/10 bg-[#1b1c20] text-gray-300"}`}
                aria-label={product.isFavorite ? "찜 해제" : "찜하기"}
              >
                <Heart className={`h-5 w-5 ${product.isFavorite ? "fill-current" : ""}`} />
              </button>
            </div>
            <div className="mt-6">
              <label className="mb-2 block text-xs font-bold text-gray-400">적용할 서버</label>
              <ServerSelector servers={servers} value={selectedServerId} onChange={onServerChange} />
            </div>
            {!product.isOwned && (
              <div className="mt-5 border-y border-white/10 py-4 text-xs leading-5 text-gray-400">
                <strong className="block text-sm text-white">판매자 및 거래 정보</strong>
                {isPlatformProduct ? (
                  <div className="mt-2 space-y-1">
                    <p>판매자: 씨에스소프트 · 대표 정원우</p>
                    <p>사업자등록번호 425-43-01340 · 010-9174-6988</p>
                    <p>충청북도 진천군 덕산읍 대월로 127</p>
                  </div>
                ) : isBusinessSeller ? (
                  <div className="mt-2 space-y-1">
                    <p>판매자 유형: 사업자 · {seller.businessName || seller.displayName}</p>
                    <p>대표자 {seller.legalName} · 사업자등록번호 {seller.businessRegistrationNumber}</p>
                    <p>{seller.address}</p>
                    <p>{seller.phone} · {seller.email}</p>
                  </div>
                ) : (
                  <div className="mt-2 space-y-1">
                    <p>판매자 유형: 개인 판매자 · {seller.displayName}</p>
                    <p>분쟁 발생 시 Disparty를 통해 판매자 신원 확인과 연락을 요청할 수 있습니다.</p>
                  </div>
                )}
                {!isPlatformProduct && (
                  <p className="mt-3 text-gray-500">씨에스소프트는 통신판매중개자이며 거래 당사자가 아닙니다. 결제, 환불 접수와 분쟁 처리 창구는 Disparty가 제공합니다.</p>
                )}
              </div>
            )}
            {user && !product.isOwned && !paymentOrder && (
              <div className="mt-5 space-y-3 text-xs leading-5 text-gray-300">
                <label className="flex cursor-pointer items-start gap-2">
                  <input type="checkbox" checked={purchaseConsents.purchaseTermsAccepted} onChange={(event) => setConsent("purchaseTermsAccepted", event.target.checked)} className="mt-1 accent-[#5865F2]" />
                  <span>상품, 가격, 판매자, 적용 서버를 확인했고 <Link to="/ko/terms" target="_blank" className="text-[#aeb5ff] underline underline-offset-4">이용약관</Link>과 <Link to="/ko/refund-policy" target="_blank" className="text-[#aeb5ff] underline underline-offset-4">환불 정책</Link>에 동의합니다.</span>
                </label>
                <label className="flex cursor-pointer items-start gap-2">
                  <input type="checkbox" checked={purchaseConsents.digitalSupplyConsent} onChange={(event) => setConsent("digitalSupplyConsent", event.target.checked)} className="mt-1 accent-[#5865F2]" />
                  <span>결제 직후 선택한 서버에 디지털 상품이 즉시 적용되는 것에 동의하며, 적용 후에는 단순 변심 청약철회가 제한될 수 있음을 확인합니다.</span>
                </label>
                <label className="flex cursor-pointer items-start gap-2">
                  <input type="checkbox" checked={purchaseConsents.intermediaryNoticeAccepted} onChange={(event) => setConsent("intermediaryNoticeAccepted", event.target.checked)} className="mt-1 accent-[#5865F2]" />
                  <span>{isPlatformProduct ? "씨에스소프트가 이 상품의 직접 판매자임을 확인합니다." : "크리에이터가 판매자이고 씨에스소프트는 통신판매중개자임을 확인합니다."}</span>
                </label>
              </div>
            )}
            {!user ? (
              <a href="https://disparty.com/login" className="mt-5 flex h-12 w-full items-center justify-center rounded-lg bg-[#5865F2] font-bold text-white hover:bg-[#4f5bd5]">
                Discord로 로그인
              </a>
            ) : product.isOwned ? (
              <button
                type="button"
                disabled={!selectedServerId || working}
                onClick={onApply}
                className="mt-5 h-12 w-full rounded-lg bg-emerald-600 font-bold text-white transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:bg-gray-700"
              >
                {working ? "적용 중..." : "선택한 서버에 적용"}
              </button>
            ) : paymentOrder ? (
              <PaymentWidget
                clientKey={paymentOrder.clientKey}
                price={paymentOrder.amount}
                orderId={paymentOrder.orderId}
                orderName={paymentOrder.orderName}
                customerName={user.username}
                successPath="/ko?tab=shop"
                failPath="/ko?tab=shop"
              />
            ) : (
              <button
                type="button"
                disabled={!selectedServerId || working || !allPurchaseConsentsAccepted}
                onClick={() => onBuy(purchaseConsents)}
                className="mt-5 h-12 w-full rounded-lg bg-[#5865F2] font-bold text-white transition-colors hover:bg-[#4f5bd5] disabled:cursor-not-allowed disabled:bg-gray-700"
              >
                {working ? "주문 만드는 중..." : `${formatPrice(product.price)}에 구입하기`}
              </button>
            )}
            {message && <p className="mt-3 text-sm text-amber-200">{message}</p>}
            <div className="mt-6 flex items-start gap-2 text-xs leading-5 text-gray-400">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
              결제 후 선택한 서버에 즉시 적용됩니다. 구매 내역은 계정에 보관됩니다.
            </div>
          </div>
        </aside>

        <div className="relative order-1 hidden items-center justify-center overflow-y-auto bg-[#0f1013] p-8 md:flex">
          <div className="w-full max-w-[470px]">
            {previewServer ? (
              <div className="pointer-events-none">
                <ServerCard server={previewServer} locale="ko" preview previewWidth={470} />
              </div>
            ) : (
              <MarketplacePreviewCard product={product} />
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="닫기"
          className="absolute right-4 top-4 z-50 flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-black/70 text-white hover:bg-black"
        >
          <X className="h-5 w-5" />
        </button>
      </motion.section>
    </motion.div>,
    document.body
  );
}

function PurchaseHistory({ purchases, onRefund, refunding }) {
  const [open, setOpen] = useState(false);
  const [reasonByOrder, setReasonByOrder] = useState({});
  if (!purchases.length) return null;

  return (
    <section className="mt-12 border-t border-[#33353b] pt-7">
      <button type="button" onClick={() => setOpen((value) => !value)} className="flex w-full items-center justify-between text-left">
        <span className="flex items-center gap-2 text-lg font-bold text-white"><History className="h-5 w-5" /> 구매 내역</span>
        <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="mt-4 overflow-x-auto rounded-lg border border-[#35373e]">
              <table className="w-full min-w-[700px] text-left text-sm">
                <thead className="bg-[#24252a] text-xs text-gray-400">
                  <tr><th className="px-4 py-3">상품</th><th className="px-4 py-3">결제일</th><th className="px-4 py-3">금액</th><th className="px-4 py-3">상태</th><th className="px-4 py-3">환불</th></tr>
                </thead>
                <tbody className="divide-y divide-[#303139] bg-[#1c1d21] text-gray-200">
                  {purchases.map((purchase) => (
                    <tr key={purchase.orderId}>
                      <td className="px-4 py-3">
                        <strong className="block font-semibold">{purchase.productName}</strong>
                        <span className="mt-1 block text-[11px] text-gray-500">판매자 {purchase.sellerName || "Disparty"} · 주문 {purchase.orderId}</span>
                        {purchase.termsVersion && <Link to="/ko/terms" target="_blank" className="mt-1 block text-[11px] text-[#9ea6ff] hover:text-white">거래 약관 {purchase.termsVersion}</Link>}
                      </td>
                      <td className="px-4 py-3 text-gray-400">{formatDate(purchase.purchasedAt)}</td>
                      <td className="px-4 py-3">{formatPrice(purchase.amount)}</td>
                      <td className="px-4 py-3">{purchase.isActive ? "사용 가능" : "환불 완료"}</td>
                      <td className="px-4 py-3">
                        {purchase.isActive && ["NONE", "REJECTED"].includes(purchase.refundStatus) ? (
                          <div className="flex gap-2">
                            <input
                              value={reasonByOrder[purchase.orderId] || ""}
                              onChange={(event) => setReasonByOrder((prev) => ({ ...prev, [purchase.orderId]: event.target.value }))}
                              placeholder="환불 사유 5자 이상"
                              className="h-9 min-w-[180px] rounded-md border border-[#41434c] bg-[#25262b] px-3 text-xs text-white outline-none focus:border-[#5865F2]"
                            />
                            <button
                              type="button"
                              disabled={refunding === purchase.orderId}
                              onClick={() => onRefund(purchase.orderId, reasonByOrder[purchase.orderId] || "")}
                              className="rounded-md border border-[#4a4c55] px-3 text-xs font-bold hover:bg-white/5 disabled:opacity-50"
                            >요청</button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">{purchase.refundStatus === "REQUESTED" ? "검토 중" : purchase.refundStatus}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export default function ShopTest({ embedded = false }) {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [servers, setServers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedServerId, setSelectedServerId] = useState("");
  const [sort, setSort] = useState("popular");
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [onlyOwned, setOnlyOwned] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [refunding, setRefunding] = useState("");
  const [activeShopSection, setActiveShopSection] = useState("store");
  const [shopSectionDirection, setShopSectionDirection] = useState(1);

  const changeShopSection = useCallback((nextSection) => {
    if (nextSection === activeShopSection) return;
    setShopSectionDirection(nextSection === "creator" ? 1 : -1);
    setActiveShopSection(nextSection);
    if (nextSection === "creator") setSelectedProductId("");
  }, [activeShopSection]);

  const loadProducts = useCallback(async () => {
    try {
      const data = await apiRequest("/api/shop/products");
      setProducts(Array.isArray(data) ? data.filter((product) => product.id !== "neon-grid") : []);
      setError("");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  const loadPurchases = useCallback(async () => {
    try {
      const data = await apiRequest("/api/shop/purchases");
      setPurchases(Array.isArray(data) ? data : []);
    } catch (_error) {
      setPurchases([]);
    }
  }, []);

  useEffect(() => {
    loadProducts();
    apiRequest("/me")
      .then(async (me) => {
        setUser(me);
        const [myServers] = await Promise.all([
          apiRequest("/api/servers/mine").catch(() => []),
          loadPurchases(),
        ]);
        const nextServers = Array.isArray(myServers) ? myServers : [];
        setServers(nextServers);
        setSelectedServerId(nextServers[0]?.server_id || "");
      })
      .catch(() => setUser(null));
  }, [loadProducts, loadPurchases]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get("checkout");
    const paymentKey = params.get("paymentKey");
    const orderId = params.get("orderId");
    const amount = Number(params.get("amount") || 0);
    if (!checkout) return;

    const clearCheckoutParams = () => {
      const url = new URL(window.location.href);
      ["checkout", "paymentKey", "orderId", "amount", "code", "message"].forEach((key) => url.searchParams.delete(key));
      window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
    };

    if (checkout === "success" && paymentKey && orderId && amount > 0) {
      setWorking(true);
      setMessage("결제 승인을 확인하고 있습니다...");
      apiRequest("/api/payments/verify", {
        method: "POST",
        body: JSON.stringify({ paymentKey, orderId, amount }),
      })
        .then(() => Promise.all([loadProducts(), loadPurchases()]))
        .then(() => setMessage("결제가 완료되었습니다. 선택한 서버에 적용했어요."))
        .catch((requestError) => setMessage(requestError.message))
        .finally(() => {
          setWorking(false);
          clearCheckoutParams();
        });
    } else {
      setMessage(params.get("message") || "결제가 취소되었거나 완료되지 않았습니다.");
      clearCheckoutParams();
    }
  }, [location.search, loadProducts, loadPurchases]);

  const visibleProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const next = products.filter((product) => {
      const haystack = [product.name, product.description, product.seller?.displayName, ...(product.tags || [])].join(" ").toLowerCase();
      return (!normalizedQuery || haystack.includes(normalizedQuery))
        && (!onlyFavorites || product.isFavorite)
        && (!onlyOwned || product.isOwned);
    });
    next.sort((a, b) => {
      if (sort === "newest") return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      if (sort === "price-low") return a.price - b.price;
      if (sort === "price-high") return b.price - a.price;
      return (b.salesCount || 0) - (a.salesCount || 0) || (b.favoriteCount || 0) - (a.favoriteCount || 0);
    });
    return next;
  }, [products, query, onlyFavorites, onlyOwned, sort]);

  const selectedProduct = products.find((product) => product.id === selectedProductId);

  const toggleFavorite = async (product) => {
    if (!user) {
      window.location.href = "https://disparty.com/login";
      return;
    }
    try {
      const result = await apiRequest(`/api/shop/products/${product.id}/favorite`, { method: "POST" });
      setProducts((items) => items.map((item) => item.id === product.id
        ? { ...item, isFavorite: result.isFavorite, favoriteCount: result.favoriteCount }
        : item));
    } catch (requestError) {
      setMessage(requestError.message);
    }
  };

  const startPayment = async (purchaseConsents) => {
    if (!selectedProduct || !selectedServerId) return;
    if (!purchaseConsents || !Object.values(purchaseConsents).every(Boolean)) {
      setMessage("구매 전 필수 확인 및 동의를 완료해주세요.");
      return;
    }
    setWorking(true);
    setMessage("");
    try {
      const order = await apiRequest("/api/payments/orders", {
        method: "POST",
        body: JSON.stringify({
          productId: selectedProduct.id,
          serverId: selectedServerId,
          locale: "ko",
          ...purchaseConsents,
        }),
      });
      setPaymentOrder(order);
    } catch (requestError) {
      setMessage(requestError.message);
    } finally {
      setWorking(false);
    }
  };

  const applyOwnedProduct = async () => {
    if (!selectedProduct || !selectedServerId) return;
    setWorking(true);
    try {
      await apiRequest("/api/shop/background/apply", {
        method: "POST",
        body: JSON.stringify({ productId: selectedProduct.id, serverId: selectedServerId }),
      });
      setMessage("선택한 서버에 적용했습니다.");
    } catch (requestError) {
      setMessage(requestError.message);
    } finally {
      setWorking(false);
    }
  };

  const requestRefund = async (orderId, reason) => {
    if (reason.trim().length < 5) {
      alert("환불 사유를 5자 이상 입력해주세요.");
      return;
    }
    setRefunding(orderId);
    try {
      await apiRequest(`/api/shop/orders/${orderId}/refund`, { method: "POST", body: JSON.stringify({ reason }) });
      await loadPurchases();
    } catch (requestError) {
      alert(requestError.message);
    } finally {
      setRefunding("");
    }
  };

  const openProduct = (product) => {
    setSelectedProductId(product.id);
    setPaymentOrder(null);
    setMessage("");
  };

  return (
    <>
      <main className={`mx-auto w-full max-w-7xl px-4 sm:px-6 ${embedded ? "pb-10 pt-0" : "py-10"}`}>
        <header className="border-b border-[#33353b] pb-6 pt-4">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <ShoppingBag className="h-7 w-7 text-[#8e97ff]" />
                <h1 className="font-maple text-3xl font-normal text-white">Disparty Shop!</h1>
              </div>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-400">크리에이터가 만든 서버 카드 디자인으로 내 서버를 더 눈에 띄게 꾸며보세요.</p>
            </div>
            {message && <p className="rounded-lg border border-[#5865F2]/30 bg-[#5865F2]/10 px-4 py-2 text-sm text-[#c9ceff]">{message}</p>}
          </div>
        </header>

        <div className="mt-6 flex items-center gap-1 overflow-x-auto border-b border-[#33353b]">
          <button
            type="button"
            onClick={() => changeShopSection("store")}
            className={`relative flex shrink-0 items-center gap-2 px-4 pb-3 text-sm font-bold transition-colors ${activeShopSection === "store" ? "text-white" : "text-gray-500 hover:text-gray-200"}`}
          >
            <Store className="h-4 w-4" /> 상품 둘러보기
            {activeShopSection === "store" && (
              <motion.span
                layoutId="shop-section-indicator"
                className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[#5865F2]"
                transition={reduceMotion ? { duration: 0 } : { duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
              />
            )}
          </button>
          <button
            type="button"
            onClick={() => changeShopSection("creator")}
            className={`relative flex shrink-0 items-center gap-2 px-4 pb-3 text-sm font-bold transition-colors ${activeShopSection === "creator" ? "text-white" : "text-gray-500 hover:text-gray-200"}`}
          >
            <ImagePlus className="h-4 w-4" /> 상품 등록
            {activeShopSection === "creator" && (
              <motion.span
                layoutId="shop-section-indicator"
                className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[#5865F2]"
                transition={reduceMotion ? { duration: 0 } : { duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
              />
            )}
          </button>
        </div>

        <AnimatePresence initial={false} mode="wait" custom={shopSectionDirection}>
          <motion.div
            key={activeShopSection}
            custom={shopSectionDirection}
            initial={reduceMotion ? false : { opacity: 0, x: shopSectionDirection * 16, y: 3 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: shopSectionDirection * -10, y: -2 }}
            transition={{ duration: reduceMotion ? 0 : 0.18, ease: [0.2, 0.8, 0.2, 1] }}
            className="min-h-[420px] transform-gpu will-change-transform"
          >
        {activeShopSection === "store" ? <>
        <section className="mt-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-black text-white">서버 카드 디자인 <span className="text-gray-500">({visibleProducts.length})</span></h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-lg border border-[#3a3c44] bg-[#24252a] px-3 md:w-64 md:flex-none">
                <Search className="h-4 w-4 text-gray-500" />
                <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="상품 또는 크리에이터 검색" className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-gray-600" />
              </label>
              <SelectMenu value={sort} options={SORT_OPTIONS} onChange={setSort} ariaLabel="상품 정렬" />
              <button type="button" onClick={() => setFiltersOpen((value) => !value)} className={`flex h-11 items-center gap-2 rounded-lg border px-4 text-sm font-semibold ${filtersOpen || onlyFavorites || onlyOwned ? "border-[#5865F2] bg-[#5865F2]/10 text-white" : "border-[#3a3c44] bg-[#24252a] text-gray-200"}`}>
                <SlidersHorizontal className="h-4 w-4" /> 필터
              </button>
            </div>
          </div>
          <AnimatePresence initial={false}>
            {filtersOpen && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                <div className="mt-3 flex flex-wrap gap-4 rounded-lg border border-[#34363d] bg-[#202126] p-4 text-sm text-gray-200">
                  <span className="flex items-center gap-2 font-bold text-white"><Filter className="h-4 w-4" /> 표시할 상품</span>
                  <label className="flex cursor-pointer items-center gap-2"><input type="checkbox" checked={onlyFavorites} onChange={(event) => setOnlyFavorites(event.target.checked)} className="accent-[#5865F2]" /> 찜한 상품</label>
                  <label className="flex cursor-pointer items-center gap-2"><input type="checkbox" checked={onlyOwned} onChange={(event) => setOnlyOwned(event.target.checked)} className="accent-[#5865F2]" /> 구매한 상품</label>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {productsLoading ? (
            <div className="grid grid-cols-1 gap-5 pt-6 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((item) => <div key={item} className="aspect-[4/5] animate-pulse rounded-lg bg-[#24252a]" />)}
            </div>
          ) : error ? (
            <div className="mt-6 rounded-lg border border-red-500/20 bg-red-500/10 p-6 text-center text-red-200">{error}</div>
          ) : visibleProducts.length ? (
            <motion.div layout={!reduceMotion} className="grid grid-cols-1 gap-5 pt-6 sm:grid-cols-2 lg:grid-cols-3">
              {visibleProducts.map((product) => (
                <ProductTile key={product.id} product={product} loggedIn={Boolean(user)} onOpen={() => openProduct(product)} onFavorite={() => toggleFavorite(product)} />
              ))}
            </motion.div>
          ) : (
            <div className="mt-6 rounded-lg border border-dashed border-[#41434b] py-20 text-center text-gray-500">조건에 맞는 상품이 없습니다.</div>
          )}
        </section>

        <PurchaseHistory purchases={purchases} onRefund={requestRefund} refunding={refunding} />
        <div className="mt-10"><RefundPolicy locale="ko" /></div>
        </> : (
          <section className="mt-6 overflow-hidden rounded-lg border border-[#30323a] bg-[#111216]">
            <ServerCardStudio embedded />
          </section>
        )}
          </motion.div>
        </AnimatePresence>
      </main>
      {!embedded && <BusinessFooter locale="ko" />}

      <AnimatePresence>
        {selectedProduct && (
          <ProductDetail
            product={selectedProduct}
            user={user}
            servers={servers}
            selectedServerId={selectedServerId}
            onServerChange={(value) => { setSelectedServerId(value); setPaymentOrder(null); }}
            onClose={() => setSelectedProductId("")}
            onFavorite={() => toggleFavorite(selectedProduct)}
            onBuy={startPayment}
            onApply={applyOwnedProduct}
            paymentOrder={paymentOrder}
            working={working}
            message={message}
          />
        )}
      </AnimatePresence>
    </>
  );
}
